import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Nav,
  SelectPicker,
  Button,
  Table,
  IconButton,
  useToaster,
  Message,
  Whisper,
  Tooltip,
  Divider,
  Heading,
  DatePicker,
  Stack,
  Toggle,
  Loader,
  Panel,
  Checkbox,
  TimePicker,
  Input
} from 'rsuite';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { fetchSetting, updateSetting } from '../../../services/setting.service';
import { getUsersWithoutDispatch } from '../../../services/user.service';
import { getCrons, manageCron } from '@/services/cron.service';
import TrashIcon from '@rsuite/icons/Trash';
import CheckIcon from '@rsuite/icons/Check';
import InfoRoundIcon from '@rsuite/icons/InfoRound';
import { useAppDispatch } from '@/config/hooks';
import { Save } from '@rsuite/icons';
import Textarea from '@/components/Textarea';
import { v4 as uuidv4 } from 'uuid';

const { Column, HeaderCell, Cell } = Table;

interface Cron {
  id: string;
  type: string;
  schedule: {
    minute: string;
    hour: string;
    dayOfMonth: string;
    month: string;
    dayOfWeek: string;
  };
  fieldTitle: string;
  message?: string;
  enabled: boolean;
  inactiveDays?: number;
  isModified?: boolean;
}

const ConfigurationDrawer = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [active, setActive] = useState('untraced_reports');
  const [untracedUsers, setUntracedUsers] = useState([]);
  const [mailUsers, setMailUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUntracedUser, setSelectedUntracedUser] = useState(null);
  const [selectedMailUser, setSelectedMailUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [untracedSettingId, setUntracedSettingId] = useState(null);
  const [mailSettingId, setMailSettingId] = useState(null);
  const [initialCron, setInitialCron] = useState<Cron | null>(null);
  const toaster = useToaster();

  const [crons, setCrons] = useState<Cron[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setCrons([]);
    if ((active === 'untraced_reports' || active == 'notification_managers') && open) {
      fetchMailUsers();
      fetchUntracedUsers();
      fetchAllUsers();
      dispatch(getCrons())
        .then(data => {
          if (typeof data === 'string') {
            toaster.push(
              <Message type="error" duration={5000} showIcon>
                {t(data)}
              </Message>,
              { placement: 'topCenter' }
            );
            setCrons([]);
            return;
          }
          const filteredCrons = Array.isArray(data)
            ? data
                .filter(cron => {
                  const type = active == 'untraced_reports' ? 'untraced' : 'untraced_teams';
                  const matches = cron && cron.type === type;
                  return matches;
                })
                .map(cron => ({
                  id: cron._id?.toString() || uuidv4(),
                  type: cron.type,
                  schedule: {
                    minute: cron.schedule?.minute || '*',
                    hour: cron.schedule?.hour || '*',
                    dayOfMonth: cron.schedule?.dayOfMonth || '*',
                    month: cron.schedule?.month || '*',
                    dayOfWeek: cron.schedule?.dayOfWeek || '*'
                  },
                  fieldTitle: cron.fieldTitle || '',
                  message: cron.message || '',
                  enabled: cron.enabled !== undefined ? cron.enabled : true,
                  inactiveDays: cron.type === 'block_users' ? cron.inactiveDays : undefined,
                  isModified: false
                }))
            : [];
          setCrons(filteredCrons);
          const newOpenPanels = new Map<string, boolean>();
          const newExpandedPanels = new Map<string, boolean>();
          filteredCrons.forEach(cron => {
            newOpenPanels.set(cron.id, true);
            newExpandedPanels.set(cron.id, true);
          });
        })
        .catch(err => {
          console.error('Error fetching crons:', err.message);
          toaster.push(
            <Message type="error" duration={5000} showIcon>
              {t(err.message || 'Failed to load notifications')}
            </Message>,
            { placement: 'topCenter' }
          );
          setCrons([]);
        });
    } else if (active === 'daily_untraced' && open) {
      fetchCrons();
    }
  }, [active, open]);

  const fetchUntracedUsers = async () => {
    setLoading(true);
    try {
      const setting = await fetchSetting('untraced_to_ignore');
      if (setting && setting.users) {
        setUntracedUsers(setting.users);
        setUntracedSettingId(setting._id);
      } else {
        toaster.push(
          <Message showIcon type="error" header={t('Error')}>
            {t('No settings found for untraced_to_ignore')}
          </Message>,
          { duration: 3000 }
        );
      }
    } catch (error) {
      console.error('Error fetching untraced_to_ignore:', error);
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('Failed to fetch untraced users')}
        </Message>,
        { duration: 3000 }
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchMailUsers = async () => {
    setLoading(true);
    try {
      const setting = await fetchSetting('Users_no_trace_Notification');
      if (setting && setting.users) {
        setMailUsers(setting.users);
        setMailSettingId(setting._id);
      } else {
        toaster.push(
          <Message showIcon type="error" header={t('Error')}>
            {t('No settings found for Users_no_trace_Notification')}
          </Message>,
          { duration: 3000 }
        );
      }
    } catch (error) {
      console.error('Error fetching Users_no_trace_Notification:', error);
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('Failed to fetch mail receivers')}
        </Message>,
        { duration: 3000 }
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const userData = await getUsersWithoutDispatch();
      setUsers(userData);
    } catch (error) {
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('Failed to fetch users')}
        </Message>,
        { duration: 3000 }
      );
    }
  };

  const fetchCrons = async () => {
    setLoading(true);
    try {
      const data = await dispatch(getCrons());
      if (typeof data === 'string') {
        toaster.push(
          <Message type="error" duration={5000} showIcon>
            {t(data)}
          </Message>,
          { placement: 'topCenter' }
        );
        setCrons(null);
        setInitialCron(null);
        return;
      }
      const filteredCron = Array.isArray(data)
        ? data.find(cron => cron && cron.type === 'untraced_daily')
        : null;
      const newCron = filteredCron
        ? {
            id: filteredCron._id?.toString() || uuidv4(),
            type: filteredCron.type || 'untraced_daily',
            schedule: {
              minute: filteredCron.schedule?.minute || '*',
              hour: filteredCron.schedule?.hour || '*',
              dayOfMonth: filteredCron.schedule?.dayOfMonth || '*',
              month: filteredCron.schedule?.month || '*',
              dayOfWeek: filteredCron.schedule?.dayOfWeek || '*'
            },
            fieldTitle: filteredCron.fieldTitle || '',
            message: filteredCron.message || '',
            enabled: filteredCron.enabled !== undefined ? filteredCron.enabled : true,
            inactiveDays: undefined,
            isModified: false
          }
        : {
            id: uuidv4(),
            type: 'untraced_daily',
            schedule: { minute: '*', hour: '*', dayOfMonth: '*', month: '*', dayOfWeek: '*' },
            fieldTitle: '',
            message: '',
            enabled: true,
            isModified: true
          };
      setCrons([newCron]);
      setInitialCron(JSON.parse(JSON.stringify(newCron)));
    } catch (err) {
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t(err.message || 'Failed to load notifications')}
        </Message>,
        { placement: 'topCenter' }
      );
      setCrons(null);
      setInitialCron(null);
    } finally {
      setLoading(false);
    }
  };

  const updateCron = (id: string, field: keyof Cron | string, value: any) => {
    setCrons(prev =>
      prev.map(c => {
        if (c.id === id) {
          if (field.startsWith('schedule.')) {
            const scheduleField = field.split('.')[1] as keyof Cron['schedule'];
            return { ...c, schedule: { ...c.schedule, [scheduleField]: value }, isModified: true };
          }
          return { ...c, [field]: value, isModified: true };
        }
        return c;
      })
    );
  };

  const handleAddUntracedUser = async () => {
    if (!selectedUntracedUser) {
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('Please select a user')}
        </Message>,
        { duration: 3000 }
      );
      return;
    }

    const user = users.find(u => u._id === selectedUntracedUser);
    if (!user || !user.registrationNumber) {
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('Invalid user selected')}
        </Message>,
        { duration: 3000 }
      );
      return;
    }

    const registrationNumber = user.registrationNumber;
    if (untracedUsers.includes(registrationNumber)) {
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('User already in the list')}
        </Message>,
        { duration: 3000 }
      );
      return;
    }

    if (!untracedSettingId) {
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('Settings document not found')}
        </Message>,
        { duration: 3000 }
      );
      return;
    }

    setLoading(true);
    try {
      const updatedUsers = [...untracedUsers, registrationNumber];
      await updateSetting(untracedSettingId, { users: updatedUsers });
      setUntracedUsers(updatedUsers);
      setSelectedUntracedUser(null);
      toaster.push(
        <Message showIcon type="success" header={t('Success')}>
          {t('User added successfully')}
        </Message>,
        { duration: 3000 }
      );
    } catch (error) {
      console.error('Error adding untraced user:', error);
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('Failed to add user')}
        </Message>,
        { duration: 3000 }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAddMailUser = async () => {
    if (!selectedMailUser) {
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('Please select a user')}
        </Message>,
        { duration: 3000 }
      );
      return;
    }

    const user = users.find(u => u._id === selectedMailUser);
    if (!user || !user._id) {
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('Invalid user selected')}
        </Message>,
        { duration: 3000 }
      );
      return;
    }

    const userId = user._id;
    if (mailUsers.includes(userId)) {
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('User already in the list')}
        </Message>,
        { duration: 3000 }
      );
      return;
    }

    if (!mailSettingId) {
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('Settings document not found')}
        </Message>,
        { duration: 3000 }
      );
      return;
    }

    setLoading(true);
    try {
      const updatedUsers = [...mailUsers, userId];
      await updateSetting(mailSettingId, { users: updatedUsers });
      setMailUsers(updatedUsers);
      setSelectedMailUser(null);
      toaster.push(
        <Message showIcon type="success" header={t('Success')}>
          {t('User added successfully')}
        </Message>,
        { duration: 3000 }
      );
    } catch (error) {
      console.error('Error adding mail user:', error);
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('Failed to add user')}
        </Message>,
        { duration: 3000 }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUntracedUser = async registrationNumber => {
    if (!untracedSettingId) {
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('Settings document not found')}
        </Message>,
        { duration: 3000 }
      );
      return;
    }

    setLoading(true);
    try {
      const updatedUsers = untracedUsers.filter(u => u !== registrationNumber);
      await updateSetting(untracedSettingId, { users: updatedUsers });
      setUntracedUsers(updatedUsers);
      toaster.push(
        <Message showIcon type="success" header={t('Success')}>
          {t('User removed successfully')}
        </Message>,
        { duration: 3000 }
      );
    } catch (error) {
      console.error('Error deleting untraced user:', error);
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('Failed to remove user')}
        </Message>,
        { duration: 3000 }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMailUser = async userId => {
    if (!mailSettingId) {
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('Settings document not found')}
        </Message>,
        { duration: 3000 }
      );
      return;
    }

    setLoading(true);
    try {
      const updatedUsers = mailUsers.filter(u => u !== userId);
      await updateSetting(mailSettingId, { users: updatedUsers });
      setMailUsers(updatedUsers);
      toaster.push(
        <Message showIcon type="success" header={t('Success')}>
          {t('User removed successfully')}
        </Message>,
        { duration: 3000 }
      );
    } catch (error) {
      console.error('Error deleting mail user:', error);
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('Failed to remove user')}
        </Message>,
        { duration: 3000 }
      );
    } finally {
      setLoading(false);
    }
  };

  const untracedUserOptions = users
    .filter(user => !user.isBlocked)
    .map(user => ({
      label: `${user.firstname} ${user.lastname} (${user.registrationNumber})`,
      value: user._id,
      disabled: untracedUsers.includes(user.registrationNumber)
    }));

  const mailUserOptions = users.map(user => ({
    label: `${user.firstname} ${user.lastname} (${user.registrationNumber})`,
    value: user._id,
    disabled: mailUsers.includes(user._id)
  }));

  const untracedTableData = untracedUsers.map(regNumber => {
    const user = users.find(u => u.registrationNumber === regNumber);
    return {
      registrationNumber: regNumber,
      firstname: user ? user.firstname : 'Unknown',
      lastname: user ? user.lastname : 'Unknown'
    };
  });

  const mailTableData = mailUsers.map(userId => {
    const user: any = users.find(u => u._id === userId);
    return {
      userId,
      firstname: user ? user.firstname : 'Unknown',
      lastname: user ? user.lastname : 'Unknown',
      registrationNumber: user ? user.registrationNumber : 'Unknown'
    };
  });
  const handleSave = async () => {
    const modifiedCrons = crons.filter(cron => cron.isModified);

    const validationErrors: string[] = [];
    modifiedCrons.forEach(cron => {
      if (!cron.fieldTitle) {
        validationErrors.push(`Notification ${cron.id}: Title is required`);
      }
    });

    if (validationErrors.length > 0) {
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t(validationErrors.join('; '))}
        </Message>,
        { placement: 'topCenter' }
      );
      return;
    }

    setIsLoading(true);
    try {
      const savePromises = modifiedCrons.map(async cron => {
        const cronData = {
          _id: cron.id.includes('-') ? undefined : cron.id,
          type: cron.type,
          schedule: {
            minute: cron.schedule.minute,
            hour: cron.schedule.hour,
            dayOfMonth: cron.schedule.dayOfMonth,
            month: cron.schedule.month,
            dayOfWeek: cron.schedule.dayOfWeek
          },
          fieldTitle: cron.fieldTitle,
          message: cron.message,
          enabled: cron.enabled
        };
        console.log(`Saving cron: ${JSON.stringify(cronData)}`);
        return dispatch(manageCron(cronData));
      });

      const saveResults = await Promise.all(savePromises);
      const saveErrors = saveResults.filter(result => typeof result === 'string');

      if (saveErrors.length > 0) {
        toaster.push(
          <Message type="error" duration={5000} showIcon>
            {t(saveErrors.join('; '))}
          </Message>,
          { placement: 'topCenter' }
        );
        return;
      }

      toaster.push(
        <Message type="success" duration={5000} showIcon>
          {t('Updated Successfully')}{' '}
        </Message>,
        { placement: 'topCenter' }
      );
      onClose();
    } catch (err) {
      console.error('Error saving crons:', err.message);
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t(err.message || 'Error saving notifications')}
        </Message>,
        { placement: 'topCenter' }
      );
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <Drawer open={open} onClose={onClose} size="md">
      <Drawer.Header>
        <Drawer.Title>{t('Configuration')}</Drawer.Title>
      </Drawer.Header>
      <Drawer.Body>
        <Nav appearance="tabs" activeKey={active} onSelect={setActive} style={{ marginBottom: 20 }}>
          <Nav.Item eventKey="untraced_reports">
            {t('Global Untraced Reporting')}{' '}
            <Whisper
              placement="top"
              trigger="hover"
              speaker={
                <Tooltip>
                  {t(
                    'Configuration for the daily Untraced Users report, along with the list of recipients who will receive it.'
                  )}
                </Tooltip>
              }
            >
              <InfoRoundIcon style={{ fontSize: 14, marginLeft: 5, verticalAlign: 'middle' }} />
            </Whisper>
          </Nav.Item>
          <Nav.Item eventKey="notification_managers">
            {t('Managers’ Team Reports')}{' '}
            <Whisper
              placement="top"
              trigger="hover"
              speaker={
                <Tooltip>
                  {t(
                    'Configuration for the daily notification to managers, with the list of Untraced Users in their respective teams.'
                  )}
                </Tooltip>
              }
            >
              <InfoRoundIcon style={{ fontSize: 14, marginLeft: 5, verticalAlign: 'middle' }} />
            </Whisper>
          </Nav.Item>
          <Nav.Item eventKey="daily_untraced">
            {t('Daily Untraced Report')}{' '}
            <Whisper
              placement="top"
              trigger="hover"
              speaker={<Tooltip>{t('Configure daily untraced user reports')}</Tooltip>}
            >
              <InfoRoundIcon style={{ fontSize: 14, marginLeft: 5, verticalAlign: 'middle' }} />
            </Whisper>
          </Nav.Item>
        </Nav>

        {active === 'untraced_reports' && (
          <div>
            <Panel
              header={
                <Stack justifyContent="space-between">
                  <span>{t('Notification Configuration – Untraced Users Report')}</span>
                  {isLoading ? (
                    <Loader content={t('Saving...')} />
                  ) : (
                    <IconButton
                      icon={<Save />}
                      appearance="primary"
                      style={{ marginTop: 10 }}
                      size="sm"
                      onClick={handleSave}
                    >
                      {t('Save')}
                    </IconButton>
                  )}
                </Stack>
              }
            >
              {crons
                .filter(cron => cron.type === 'untraced')
                .map((cron, index) => (
                  <div key={index}>
                    <div style={{ marginBottom: 20 }}>
                      <label>{t('Title')}</label>
                      <Input
                        value={cron.fieldTitle}
                        onChange={value => updateCron(cron.id, 'fieldTitle', value)}
                        placeholder={t('Enter title')}
                        disabled={!cron.enabled}
                      />
                    </div>
                    <div style={{ marginBottom: 20 }}>
                      <label>{t('Message')}</label>
                      <Textarea
                        value={cron.message || ''}
                        onChange={value => updateCron(cron.id, 'message', value)}
                        placeholder={t('Enter report message')}
                        block
                        disabled={!cron.enabled}
                      />
                    </div>
                    <div style={{ marginBottom: 20 }}>
                      <label>{t('Schedule Time')}</label>
                      <TimePicker
                        format="HH:mm"
                        placement="auto"
                        value={
                          cron.schedule.hour !== '*' && cron.schedule.minute !== '*'
                            ? new Date(
                                0,
                                0,
                                0,
                                Number(cron.schedule.hour),
                                Number(cron.schedule.minute)
                              )
                            : null
                        }
                        onChange={value => {
                          if (value) {
                            updateCron(cron.id, 'schedule.hour', value.getHours().toString());
                            updateCron(cron.id, 'schedule.minute', value.getMinutes().toString());
                          } else {
                            updateCron(cron.id, 'schedule.hour', '*');
                            updateCron(cron.id, 'schedule.minute', '*');
                          }
                        }}
                        placeholder={t('Select time')}
                        style={{ width: '100%' }}
                        disabled={!cron.enabled}
                      />
                    </div>
                    <div style={{ marginBottom: 20 }}>
                      <label>{t('Schedule Day and Month (optional)')}</label>
                      <DatePicker
                        format="d MMM"
                        placement="auto"
                        value={
                          cron.schedule.dayOfMonth !== '*' && cron.schedule.month !== '*'
                            ? new Date(
                                2025,
                                Number(cron.schedule.month) - 1,
                                Number(cron.schedule.dayOfMonth)
                              )
                            : null
                        }
                        onChange={value => {
                          if (value) {
                            updateCron(cron.id, 'schedule.dayOfMonth', value.getDate().toString());
                            updateCron(
                              cron.id,
                              'schedule.month',
                              (value.getMonth() + 1).toString()
                            );
                          } else {
                            updateCron(cron.id, 'schedule.dayOfMonth', '*');
                            updateCron(cron.id, 'schedule.month', '*');
                          }
                        }}
                        placeholder={t('Select day and month (if needed)')}
                        style={{ width: '100%' }}
                        ranges={[]}
                        cleanable={true}
                        disabled={!cron.enabled}
                      />
                    </div>
                    <div style={{ marginBottom: 20 }}>
                      <Toggle
                        size="lg"
                        checkedChildren={t('Enabled')}
                        unCheckedChildren={t('Disabled')}
                        onChange={checked => {
                          updateCron(cron.id, 'enabled', checked);
                        }}
                        checked={cron.enabled}
                      />
                    </div>
                  </div>
                ))}
            </Panel>
            <Panel header={<span>{t('Recipients of the Report')}</span>}>
              <div style={{ marginBottom: 20, display: 'flex', gap: 10 }}>
                <SelectPicker
                  data={mailUserOptions}
                  value={selectedMailUser}
                  onChange={setSelectedMailUser}
                  placeholder={t('Please select a user')}
                  style={{ width: 300 }}
                  searchable
                />
                <Button appearance="primary" onClick={handleAddMailUser} disabled={loading}>
                  <CheckIcon style={{ marginRight: 8 }} /> {t('Add User')}
                </Button>
              </div>
              <Table data={mailTableData} loading={loading} height={200}>
                <Column flexGrow={1}>
                  <HeaderCell>{t('First Name')}</HeaderCell>
                  <Cell dataKey="firstname" />
                </Column>
                <Column flexGrow={1}>
                  <HeaderCell>{t('Last Name')}</HeaderCell>
                  <Cell dataKey="lastname" />
                </Column>
                <Column flexGrow={1}>
                  <HeaderCell>{t('Registration Number')}</HeaderCell>
                  <Cell dataKey="registrationNumber" />
                </Column>
                <Column width={80}>
                  <HeaderCell>{t('Actions')}</HeaderCell>
                  <Cell>
                    {rowData => (
                      <IconButton
                        icon={<TrashIcon style={{ color: 'red' }} />}
                        appearance="subtle"
                        onClick={() => handleDeleteMailUser(rowData.userId)}
                        disabled={loading}
                      />
                    )}
                  </Cell>
                </Column>
              </Table>
            </Panel>
            <Panel
              header={
                <Stack justifyContent="space-between">
                  <span>{t('Users Excluded from Untraced Reports')}</span>
                </Stack>
              }
            >
              <div>
                <div style={{ marginBottom: 20, display: 'flex', gap: 10 }}>
                  <SelectPicker
                    data={untracedUserOptions}
                    value={selectedUntracedUser}
                    onChange={setSelectedUntracedUser}
                    placeholder={t('Please select a user')}
                    style={{ width: 300 }}
                    searchable
                  />
                  <Button appearance="primary" onClick={handleAddUntracedUser} disabled={loading}>
                    <CheckIcon style={{ marginRight: 8 }} /> {t('Add User')}
                  </Button>
                </div>
                <Table data={untracedTableData} loading={loading} height={400}>
                  <Column flexGrow={1}>
                    <HeaderCell>{t('First Name')}</HeaderCell>
                    <Cell dataKey="firstname" />
                  </Column>
                  <Column flexGrow={1}>
                    <HeaderCell>{t('Last Name')}</HeaderCell>
                    <Cell dataKey="lastname" />
                  </Column>
                  <Column flexGrow={1}>
                    <HeaderCell>{t('Registration Number')}</HeaderCell>
                    <Cell dataKey="registrationNumber" />
                  </Column>
                  <Column width={80}>
                    <HeaderCell>{t('Actions')}</HeaderCell>
                    <Cell>
                      {rowData => (
                        <IconButton
                          icon={<TrashIcon style={{ color: 'red' }} />}
                          appearance="subtle"
                          onClick={() => handleDeleteUntracedUser(rowData.registrationNumber)}
                          disabled={loading}
                        />
                      )}
                    </Cell>
                  </Column>
                </Table>
              </div>
            </Panel>
          </div>
        )}

        {active === 'notification_managers' && (
          <div>
            <Panel
              header={
                <Stack justifyContent="space-between">
                  <span>{t('Notification Configuration – Untraced Users Report by Team')}</span>
                  {isLoading ? (
                    <Loader content={t('Saving...')} />
                  ) : (
                    <IconButton
                      icon={<Save />}
                      appearance="primary"
                      style={{ marginTop: 10 }}
                      size="sm"
                      onClick={handleSave}
                    >
                      {t('Save')}
                    </IconButton>
                  )}
                </Stack>
              }
            >
              {crons
                .filter(cron => cron.type === 'untraced_teams')
                .map((cron, index) => (
                  <div key={index}>
                    <div style={{ marginBottom: 20 }}>
                      <label>{t('Title')}</label>
                      <Input
                        value={cron.fieldTitle}
                        onChange={value => updateCron(cron.id, 'fieldTitle', value)}
                        placeholder={t('Enter title')}
                        disabled={!cron.enabled}
                      />
                    </div>
                    <div style={{ marginBottom: 20 }}>
                      <label>{t('Message')}</label>
                      <Textarea
                        value={cron.message || ''}
                        onChange={value => updateCron(cron.id, 'message', value)}
                        placeholder={t('Enter report message')}
                        block
                        disabled={!cron.enabled}
                      />
                    </div>
                    <div style={{ marginBottom: 20 }}>
                      <label>{t('Schedule Time')}</label>
                      <TimePicker
                        format="HH:mm"
                        placement="auto"
                        value={
                          cron.schedule.hour !== '*' && cron.schedule.minute !== '*'
                            ? new Date(
                                0,
                                0,
                                0,
                                Number(cron.schedule.hour),
                                Number(cron.schedule.minute)
                              )
                            : null
                        }
                        onChange={value => {
                          if (value) {
                            updateCron(cron.id, 'schedule.hour', value.getHours().toString());
                            updateCron(cron.id, 'schedule.minute', value.getMinutes().toString());
                          } else {
                            updateCron(cron.id, 'schedule.hour', '*');
                            updateCron(cron.id, 'schedule.minute', '*');
                          }
                        }}
                        placeholder={t('Select time')}
                        style={{ width: '100%' }}
                        disabled={!cron.enabled}
                      />
                    </div>
                    <div style={{ marginBottom: 20 }}>
                      <label>{t('Schedule Day and Month (optional)')}</label>
                      <DatePicker
                        format="d MMM"
                        placement="auto"
                        value={
                          cron.schedule.dayOfMonth !== '*' && cron.schedule.month !== '*'
                            ? new Date(
                                2025,
                                Number(cron.schedule.month) - 1,
                                Number(cron.schedule.dayOfMonth)
                              )
                            : null
                        }
                        onChange={value => {
                          if (value) {
                            updateCron(cron.id, 'schedule.dayOfMonth', value.getDate().toString());
                            updateCron(
                              cron.id,
                              'schedule.month',
                              (value.getMonth() + 1).toString()
                            );
                          } else {
                            updateCron(cron.id, 'schedule.dayOfMonth', '*');
                            updateCron(cron.id, 'schedule.month', '*');
                          }
                        }}
                        placeholder={t('Select day and month (if needed)')}
                        style={{ width: '100%' }}
                        ranges={[]}
                        cleanable={true}
                        disabled={!cron.enabled}
                      />
                    </div>
                    <div style={{ marginBottom: 20 }}>
                      <Toggle
                        size="lg"
                        checkedChildren={t('Enabled')}
                        unCheckedChildren={t('Disabled')}
                        onChange={checked => {
                          updateCron(cron.id, 'enabled', checked);
                        }}
                        checked={cron.enabled}
                      />
                    </div>
                  </div>
                ))}
            </Panel>
          </div>
        )}
        {active === 'daily_untraced' && (
          <div>
            {loading ? (
              <Loader content={t('Loading data...')} />
            ) : (
              <>
                {crons?.length > 0 ? (
                  <div>
                    {crons?.map((cron, index) => (
                      <Panel
                        key={index}
                        header={
                          <Stack justifyContent="space-between">
                            {t('Untraced User Report')}
                            {isLoading ? (
                              <Loader content={t('Saving...')} />
                            ) : (
                              <IconButton
                                icon={<Save />}
                                appearance="primary"
                                style={{ marginTop: 10 }}
                                size="sm"
                                onClick={handleSave}
                              >
                                {t('Save')}
                              </IconButton>
                            )}
                          </Stack>
                        }
                        style={{ marginBottom: 10 }}
                      >
                        <div style={{ marginBottom: 20 }}>
                          <label>{t('Title')}</label>
                          <Input
                            value={cron.fieldTitle}
                            onChange={value => updateCron(cron.id, 'fieldTitle', value)}
                            placeholder={t('Enter title')}
                            disabled={!cron.enabled}
                          />
                        </div>
                        <div style={{ marginBottom: 20 }}>
                          <label>{t('Message')}</label>
                          <Textarea
                            value={cron.message || ''}
                            onChange={value => updateCron(cron.id, 'message', value)}
                            placeholder={t('Enter report message')}
                            block
                            disabled={!cron.enabled}
                          />
                        </div>
                        <div style={{ marginBottom: 20 }}>
                          <label>{t('Schedule Time')}</label>
                          <TimePicker
                            format="HH:mm"
                            placement="auto"
                            value={
                              cron.schedule.hour !== '*' && cron.schedule.minute !== '*'
                                ? new Date(
                                    0,
                                    0,
                                    0,
                                    Number(cron.schedule.hour),
                                    Number(cron.schedule.minute)
                                  )
                                : null
                            }
                            onChange={value => {
                              if (value) {
                                updateCron(cron.id, 'schedule.hour', value.getHours().toString());
                                updateCron(
                                  cron.id,
                                  'schedule.minute',
                                  value.getMinutes().toString()
                                );
                              } else {
                                updateCron(cron.id, 'schedule.hour', '*');
                                updateCron(cron.id, 'schedule.minute', '*');
                              }
                            }}
                            disabled={!cron.enabled}
                            placeholder={t('Select time')}
                            style={{ width: '100%' }}
                          />
                        </div>
                        <div style={{ marginBottom: 20 }}>
                          <Toggle
                            size="lg"
                            checkedChildren={t('Enabled')}
                            unCheckedChildren={t('Disabled')}
                            onChange={checked => {
                              updateCron(cron.id, 'enabled', checked);
                            }}
                            checked={cron.enabled}
                          />
                        </div>
                      </Panel>
                    ))}
                  </div>
                ) : (
                  <Message type="info" showIcon>
                    {t('No untraced user report found')}
                  </Message>
                )}
              </>
            )}
          </div>
        )}
      </Drawer.Body>
    </Drawer>
  );
};

export default ConfigurationDrawer;
